import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { publicApiClient, type PublicCoursePage as CoursePageData } from '@/api/public'
import { moneyFor } from '@/lib/money'
import { datesFor } from '@/lib/dates'
import { usePageMeta } from '@/hooks/usePageMeta'
import { BrandHeader, NotFound, PageSpinner } from './PublicCourseParts'
import { brandVars } from './brand'
import CourseDetail from './CourseDetail'

/** schema.org Course data, so search engines can show the course and its dates. */
function courseJsonLd(data: CoursePageData, tenantSlug: string): object {
  const origin = window.location.origin
  return {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: data.name,
    ...(data.summary ? { description: data.summary } : {}),
    ...(data.code ? { courseCode: data.code } : {}),
    url: `${origin}/courses/${tenantSlug}/${data.slug}`,
    provider: { '@type': 'Organization', name: data.tenantName, ...(data.website ? { sameAs: data.website } : {}) },
    ...(data.sessions.length > 0 ? {
      hasCourseInstance: data.sessions.map(s => ({
        '@type': 'CourseInstance',
        courseMode: s.deliveryMethod === 'Online' ? 'online' : 'onsite',
        startDate: s.startsAt,
        ...(s.endsAt ? { endDate: s.endsAt } : {}),
        ...(s.deliveryMethod !== 'Online' && s.venue ? {
          location: { '@type': 'Place', name: s.venue.name, address: { '@type': 'PostalAddress', ...(s.venue.city ? { addressLocality: s.venue.city } : {}) } },
        } : {}),
        ...(s.pricingModel === 'PerPerson' && s.price != null ? {
          offers: {
            '@type': 'Offer', price: s.price, priceCurrency: data.currency,
            availability: s.availablePlaces > 0 ? 'https://schema.org/InStock' : 'https://schema.org/SoldOut',
            url: `${origin}/sessions/${tenantSlug}/${s.id}`,
          },
        } : {}),
      })),
    } : {}),
  }
}

/** One course on a customer's website: pictures, the content they wrote, and its upcoming dates with a way to book. */
export default function PublicCoursePage() {
  const { slug, courseSlug } = useParams<{ slug: string; courseSlug: string }>()

  const { data, isLoading, isError } = useQuery({
    queryKey: ['public-course', slug, courseSlug],
    queryFn: () => publicApiClient.getPublicCoursePage(slug!, courseSlug!).then(r => r.data),
    enabled: !!slug && !!courseSlug,
  })
  const { fmt: fmtMoney } = moneyFor(data?.currency)
  const { formatDate, formatTime } = datesFor(data?.region, data?.timeZoneId)

  usePageMeta({
    title: data ? `${data.name} | ${data.tenantName}` : 'Course',
    description: data?.summary,
    noIndex: data?.noIndex,
    jsonLd: data && slug ? courseJsonLd(data, slug) : null,
  })

  if (isLoading) return <PageSpinner />
  if (isError || !data) return <NotFound title="Course not found" hint="It may have been removed. Check the link and try again." />

  return (
    <div className="min-h-screen bg-slate-50" style={brandVars(data)}>
      <BrandHeader
        brand={data}
        subtitle="Training courses"
        right={<Link to={`/attendee/${slug}/login`} className="hover:opacity-80">My certificates →</Link>}
      />

      <main className="max-w-5xl mx-auto px-4 py-8">
        <CourseDetail data={data} slug={slug!} fmtMoney={fmtMoney} formatDate={formatDate} formatTime={formatTime} />
      </main>
    </div>
  )
}
